import { Temporal } from 'temporal-polyfill';
import { isNonEmptyArray, isNotNullish, partitionBy } from './common-utils.js';
import {
  AdminSession,
  CoachOverallGrade,
  CoachSessionRemarkV2,
  CoachSubstitutionInvolvement,
  CoachPortalSubstitutionRequest,
  LearningLossLabelMap,
  LearningLossSelectable,
  MeetingInfo,
  MeetingInfoProps,
  MeetingProvider,
  MeetingStatus,
  OriginalTeacherIssue,
  OriginalTeacherIssueLabelMap,
  OriginalTeacherIssueSelectable,
  PlainSessionFeedback,
  RecreateSessionSummaryItem,
  RecreateSubSessionTimeChangedSummary,
  SessionStatus,
  StudentSessionLike,
  SubstitutionRequest,
  SubstitutionRequestOrigin,
  SubstitutionRequestType,
  UserLike,
  VideoConferenceType,
} from './data/index.js';
import { formatInstant } from './temporal-utils/formatting.js';
import {
  differenceInTimeUnit,
  endOfDay,
  formatShortDateWithDayOfWeek,
  formatTimeRange,
  InstantCompare,
  instantToDate,
  minInstant,
  startOfDay,
  startOfDayFromPlainDate,
} from './temporal-utils/index.js';

/** Session remarks Sub class automation window (AIR-2573). */
export const SUB_CLASS_REMARK_WITHIN_MINUTES = 12 * 60;
/** Original teacher issue Regular vs Short notice automation window (AIR-2573). */
export const ORIGINAL_TEACHER_SUB_REQUEST_WITHIN_MINUTES = 16 * 60;

export type AutoManagedOriginalTeacherIssue =
  | typeof OriginalTeacherIssue.regular_sub_request
  | typeof OriginalTeacherIssue.short_notice_sub_request;

export const autoManagedOriginalTeacherIssues = new Set<OriginalTeacherIssue>([
  OriginalTeacherIssue.regular_sub_request,
  OriginalTeacherIssue.short_notice_sub_request,
]);

export type ProposedSubstituteTeacherCandidate = {
  id: string;
  overallGrade: CoachOverallGrade | null;
};

const proposedSubstituteGradePriority: Record<NonNullable<CoachOverallGrade>, number> = {
  S: 0,
  A: 1,
  B: 2,
  C: 3,
  Never_Taught: 4,
  Not_reviewed: 5,
  D: 6,
};

/**
 * Picks the best eligible substitute for AIR-2671.
 *
 * Teachers who have taught the student group before rank above those who have
 * not. Overall grade (S > A > B > C) breaks ties within each group, then coach
 * id for deterministic results. History takes precedence over grade.
 */
export function pickProposedSubstituteTeacher<T extends ProposedSubstituteTeacherCandidate>({
  candidates,
  previouslyAssignedTeacherIds,
}: {
  candidates: T[];
  previouslyAssignedTeacherIds: ReadonlySet<string>;
}): T | null {
  const historyPriority = (coachId: string): number => (previouslyAssignedTeacherIds.has(coachId) ? 0 : 1);

  const gradePriority = (grade: CoachOverallGrade | null): number =>
    proposedSubstituteGradePriority[grade ?? 'Not_reviewed'];

  return (
    [...candidates].sort(
      (a, b) =>
        historyPriority(a.id) - historyPriority(b.id) ||
        gradePriority(a.overallGrade) - gradePriority(b.overallGrade) ||
        a.id.localeCompare(b.id),
    )[0] ?? null
  );
}

/**
 * AIR-2573 Session Remarks: classify Sub class vs Sub class (within 12 hours) from the most recent
 * fulfilled ledger event time (`fulfilledBy` / `acceptedAt`, falling back to `createdAt`).
 */
export function getDesiredSubClassRemarkV2({
  sessionStartInstant,
  fulfilledByInstant,
}: {
  sessionStartInstant: Temporal.Instant;
  fulfilledByInstant: Temporal.Instant;
}): typeof CoachSessionRemarkV2.subClass | typeof CoachSessionRemarkV2.subClassWithin12Hours {
  const minutesUntilStart = differenceInTimeUnit(fulfilledByInstant, sessionStartInstant, 'minute');
  return minutesUntilStart <= SUB_CLASS_REMARK_WITHIN_MINUTES
    ? CoachSessionRemarkV2.subClassWithin12Hours
    : CoachSessionRemarkV2.subClass;
}

/**
 * AIR-2573 Original Teacher Issue: classify Regular vs Short notice from the original teacher's
 * initial substitute request time (`requestedBy` / `createdAt`).
 */
export function getDesiredAutoOriginalTeacherIssue({
  sessionStartInstant,
  originalTeacherRequestedByInstant,
}: {
  sessionStartInstant: Temporal.Instant;
  originalTeacherRequestedByInstant: Temporal.Instant;
}): AutoManagedOriginalTeacherIssue {
  const minutesUntilStart = differenceInTimeUnit(originalTeacherRequestedByInstant, sessionStartInstant, 'minute');
  return minutesUntilStart <= ORIGINAL_TEACHER_SUB_REQUEST_WITHIN_MINUTES
    ? OriginalTeacherIssue.short_notice_sub_request
    : OriginalTeacherIssue.regular_sub_request;
}

export function getOriginalTeacherIssueLabel(issue: OriginalTeacherIssueSelectable | null | undefined): string {
  if (!issue || issue === 'null') return '(empty)';
  return OriginalTeacherIssueLabelMap[issue];
}

export function getLearningLossLabel(learningLoss: LearningLossSelectable | null | undefined): string {
  if (!learningLoss || learningLoss === 'null') return '(empty)';
  return LearningLossLabelMap[learningLoss];
}

export function normalizeOriginalTeacherIssueReason(reason: string | null | undefined): string | null {
  const trimmedReason = reason?.trim();
  return trimmedReason ? trimmedReason : null;
}

export type SubstitutionRequestSummaryInput = SubstitutionRequest | CoachPortalSubstitutionRequest;

export type SessionSubstitutionSummary<T extends SubstitutionRequestSummaryInput = SubstitutionRequest> = {
  coachIdWhoCanRequestSub: string | null;
  mostRecentTNS: T | undefined;
  mostRecentAdminRequest: T | undefined;
  mostRecentCoachRequest: T | undefined;
  mostRecentRequest: T | undefined;
  /**
   * Most recent non-canceled **fulfilled** substitution request. Used by Session Remarks V2
   * automation (AIR-2573) — Sub class tags key off this row's fulfill time, not a pending tail.
   */
  mostRecentFulfilledRequest: T | undefined;
  /**
   * Earliest non-canceled substitution request whose requester is the session's original coach
   * (`coachId`). Used for admin views that need "when did the original teacher first enter the sub
   * flow", not the latest follow-up request. Also anchors Original Teacher Issue automation (AIR-2573).
   */
  firstNonCanceledOriginalTeacherRequest: T | undefined;
  /**
   * Who is treated as assigned to teach: the session’s original `coachId` when there are no
   * substitution requests, otherwise the assignee implied by the sub-request chain. When the latest
   * non-cancelled request is still pending, this is null—the product treats the session as having
   * no teacher assigned until a substitute is found or that request is cancelled.
   */
  coachIdWhoIsTeaching: string | null;
  getSubstitutionSummaryFor: (currentCoachId: string) => {
    /**
     * Return if substitute request ID that the currentCoachId can fulfill
     * null if no valid substitute request to fulfill
     */
    fulfillableSubRequestId: string | null;
    mostRecentInvolvedRequest: T | undefined;
    involvement: CoachSubstitutionInvolvement;
    involvementMessage: string | undefined;
    firstNonCanceledOriginalTeacherRequest: T | undefined;
  };
  isCoachTNS: (coachIdToCheck: string) => boolean;

  /**
   * Given a coachId, return how many hours until the session start when the coach
   * first requested a substitution that is not cancelled.
   * @param coachId ID of the coach to check for earliest request
   * @returns number of hours until session start or null if no request found
   */
  getEarliestRequestHourUntilSessionStart: (coachId: string) => number | null;
};

export const getSessionSubstitutionRequestSummary = <T extends SubstitutionRequestSummaryInput>(session: {
  coachId: string | null;
  status: SessionStatus;
  substitutionRequests: T[];
}): SessionSubstitutionSummary<T> => {
  const { coachId: sessionOriginalCoachId, status: sessionStatus, substitutionRequests } = session;
  const sortedNonCanceledRequestsOlderToNewer = substitutionRequests
    .filter(({ status }) => status !== 'cancelled')
    .toSorted((a, b) => Temporal.Instant.compare(a.createdAtInstant, b.createdAtInstant));

  const firstNonCanceledOriginalTeacherRequest = sessionOriginalCoachId
    ? sortedNonCanceledRequestsOlderToNewer.find((request) => request.requestedById === sessionOriginalCoachId)
    : undefined;

  const mostRecentTNS = sortedNonCanceledRequestsOlderToNewer.findLast(
    ({ type }) => type === SubstitutionRequestType.coach_no_show,
  );
  const mostRecentAdminRequest = sortedNonCanceledRequestsOlderToNewer.findLast(
    ({ type }) => type === SubstitutionRequestType.assigned_by_admin,
  );
  const mostRecentCoachRequest = sortedNonCanceledRequestsOlderToNewer.findLast(
    ({ type }) => type === SubstitutionRequestType.requested_sub_by_coach,
  );

  let coachIdWhoIsTeaching: string | null = sessionOriginalCoachId;
  const mostRecentRequest = sortedNonCanceledRequestsOlderToNewer.at(-1);
  const mostRecentFulfilledRequest = sortedNonCanceledRequestsOlderToNewer.findLast(
    ({ status }) => status === 'fulfilled',
  );

  if (mostRecentRequest) {
    const { status: requestStatus, substituteId } = mostRecentRequest;
    // Pending tail: no resolved assignee (see `coachIdWhoIsTeaching` on `SessionSubstitutionSummary`).
    coachIdWhoIsTeaching = requestStatus === 'fulfilled' ? substituteId : null;
  }

  let coachIdWhoCanRequestSub: string | null = null;

  const isValidStatus =
    sessionStatus === SessionStatus.scheduled ||
    sessionStatus === SessionStatus.in_progress ||
    sessionStatus === SessionStatus.completed;

  // only allow new sub request when session has a valid status
  if (isValidStatus) {
    if (!mostRecentRequest) {
      // no request
      coachIdWhoCanRequestSub = sessionOriginalCoachId;
    } else {
      const { status: requestStatus, substituteId, requestedById } = mostRecentRequest;
      if (requestStatus === 'pending') {
        // already has a pending request, no new request allowed by anyone
        coachIdWhoCanRequestSub = null;
      } else {
        // After filtering cancelled rows, the tail is `fulfilled`. The current teacher is
        // `substituteId`; they (and admins) may open the next request. The `: requestedById` arm
        // is only for exhaustiveness if new statuses are added later.
        coachIdWhoCanRequestSub = requestStatus === 'fulfilled' ? substituteId : requestedById;
      }
    }
  }

  const getFulfillableSubRequestId = (testCoachId: string): string | null => {
    if (isValidStatus) {
      if (!mostRecentRequest || mostRecentRequest.status !== 'pending') {
        // no request or request is not pending, nothing to fulfill
        return null;
      }

      const { requestedById, type } = mostRecentRequest;
      // Coach-initiated requests cannot be self-fulfilled; TNS and admin-assigned requests can be
      // fulfilled by the same coach id that appears on the row (e.g. admin workflow).
      return requestedById !== testCoachId ||
        type === SubstitutionRequestType.coach_no_show ||
        type === SubstitutionRequestType.assigned_by_admin
        ? mostRecentRequest.id
        : null;
    }

    return null;
  };

  const getMostRecentRequest = (involvedCoachId: string) =>
    sortedNonCanceledRequestsOlderToNewer.findLast(
      ({ requestedById, substituteId }) => requestedById === involvedCoachId || substituteId === involvedCoachId,
    );

  const isCoachTNS = (coachIdToCheck: string) => {
    return sortedNonCanceledRequestsOlderToNewer.some(
      (request) => request.type === 'coach_no_show' && request.requestedById === coachIdToCheck,
    );
  };

  const getSubstitutionSummaryFor = (currentCoachId: string) => {
    const mostRecentInvolvedRequest = getMostRecentRequest(currentCoachId);
    const fulfillableSubRequestId = getFulfillableSubRequestId(currentCoachId);

    let involvement: CoachSubstitutionInvolvement = 'none';
    let involvementMessage: string | undefined;

    if (mostRecentRequest) {
      const { status } = mostRecentRequest;
      if (status === 'pending' && mostRecentRequest.requestedById === currentCoachId) {
        involvementMessage =
          'Your substitution request is pending. We will confirm with you by email once it is fulfilled.';
        involvement = 'requested_req_pending';
      }

      if (status === 'fulfilled' && mostRecentRequest.requestedById === currentCoachId) {
        involvementMessage = 'Your substitution request has been fulfilled. You will no longer be teaching this class.';
        involvement = 'requested_req_fulfilled';
      }

      if (status === 'fulfilled' && mostRecentRequest.substituteId === currentCoachId) {
        involvementMessage = 'You are teaching this lesson as a substitute for another teacher';
        involvement = 'fulfilled_request';
      }
    }

    return {
      mostRecentInvolvedRequest,
      fulfillableSubRequestId,
      involvement,
      involvementMessage,
      firstNonCanceledOriginalTeacherRequest,
    };
  };

  const getEarliestRequestHourUntilSessionStart = (coachId: string): number | null => {
    const earliestRequest = sortedNonCanceledRequestsOlderToNewer.find((request) => request.requestedById === coachId);
    return earliestRequest?.hoursUntilSessionStart ?? null;
  };

  return {
    coachIdWhoCanRequestSub,
    mostRecentRequest,
    mostRecentFulfilledRequest,
    mostRecentTNS,
    mostRecentAdminRequest,
    mostRecentCoachRequest,
    firstNonCanceledOriginalTeacherRequest,
    coachIdWhoIsTeaching,
    getSubstitutionSummaryFor,
    isCoachTNS,
    getEarliestRequestHourUntilSessionStart,
  };
};

/**
 * AIR-2988: an automatic conflict replaces the Original teacher display with "Pending substitute
 * teacher" only when the original teacher opened the pending request. If a confirmed substitute
 * opened it, the stored original teacher remains visible.
 */
export function hasPendingAutomaticConflictForOriginalTeacher(session: {
  coachId: string | null;
  status: SessionStatus;
  substitutionRequests: SubstitutionRequestSummaryInput[];
}): boolean {
  const { mostRecentRequest } = getSessionSubstitutionRequestSummary(session);
  return (
    mostRecentRequest?.status === 'pending' &&
    mostRecentRequest.origin === SubstitutionRequestOrigin.automatic_conflict &&
    mostRecentRequest.requestedById === session.coachId
  );
}

/**
 * Resolved teaching assignee for the session, or null when the substitution chain ends in a
 * pending request (no teacher assigned until fulfilled or cancelled). Otherwise follows
 * {@link getSessionSubstitutionRequestSummary} / `coachIdWhoIsTeaching`.
 *
 * @param session Session with original `coachId` and substitution request chain.
 * @returns Coach id assigned to teach, or null when the latest request is still pending.
 */
export const getTeachingCoachId = <T extends SubstitutionRequestSummaryInput>(session: {
  coachId: string | null;
  status: SessionStatus;
  substitutionRequests: T[];
}): string | null => {
  return getSessionSubstitutionRequestSummary(session).coachIdWhoIsTeaching;
};

/**
 * Coach record for who is assigned to teach the session: follows
 * {@link getSessionSubstitutionRequestSummary}'s `coachIdWhoIsTeaching`, then the matching
 * substitution request's `substituteCoach`, else `session.coach`.
 *
 * When `coachIdWhoIsTeaching` is `null` (e.g. pending substitution), falls back to `session.coach`,
 * matching the session edit flow's default for the session teacher field.
 */
export function getActualSessionTeacherCoach(session: {
  coachId: string | null;
  status: SessionStatus;
  substitutionRequests: SubstitutionRequest[];
  coach: UserLike | null;
}): UserLike | null {
  const { coachIdWhoIsTeaching } = getSessionSubstitutionRequestSummary(session);
  if (coachIdWhoIsTeaching == null) {
    return session.coach;
  }
  return (
    session.substitutionRequests.find((req) => req.substituteId === coachIdWhoIsTeaching)?.substituteCoach ||
    session.coach
  );
}

export function getSessionFormattedSchedule({
  startInstant,
  endInstant,
  displayTimeZone,
}: {
  startInstant: Temporal.Instant;
  endInstant: Temporal.Instant;
  displayTimeZone: string;
}) {
  const { formattedTime: sessionStartTimeFormatted, formattedDate: sessionDateFormatted } = formatInstant(
    startInstant,
    displayTimeZone,
    { hour12: true },
  );
  const { formattedTime: sessionEndTimeFormatted } = formatInstant(endInstant, displayTimeZone, { hour12: true });
  const sessionTimeFormatted = `${sessionStartTimeFormatted.toLowerCase()} - ${sessionEndTimeFormatted.toLowerCase()}`;
  const fullScheduleFormatted = `${sessionDateFormatted}, ${sessionTimeFormatted} (${displayTimeZone})`;

  return {
    sessionStartTimeFormatted,
    sessionEndTimeFormatted,
    sessionTimeFormatted,
    sessionDateFormatted,
    fullScheduleFormatted,
  };
}

export function getUpcomingSessions({
  sessions,
  timeZone,
}: {
  sessions: {
    startInstant: Temporal.Instant;
    endInstant: Temporal.Instant;
  }[];
  timeZone: string;
}) {
  const instant = Temporal.Now.instant();
  const startOfToday = startOfDay(instant, timeZone);
  const endOfToday = endOfDay(instant, timeZone);
  const sortedSessions = sessions
    .filter(({ startInstant }) => startInstant.epochMilliseconds >= startOfToday.epochMilliseconds)
    .toSorted((a, b) => a.startInstant.epochMilliseconds - b.startInstant.epochMilliseconds);
  const [todaySessions, futureSessions] = partitionBy(sortedSessions, (session) => {
    const start = InstantCompare(session.startInstant);
    const compareStart = start.isAfterOrEqual(Temporal.Instant.from(startOfToday));
    const compareEnd = start.isBeforeOrEqual(Temporal.Instant.from(endOfToday));
    return compareStart && compareEnd;
  });
  const nextSession = todaySessions[0] ?? futureSessions[0] ?? null;
  return { futureSessions, todaySessions, nextSession };
}

/**
 * Maps SessionStatus to MeetingStatus based on 1:1 relationship.
 * Since session status and meeting status are synchronized through actions,
 * we can directly map one to the other without complex calculations.
 */
export function sessionStatusToMeetingStatus(sessionStatus: SessionStatus): MeetingStatus {
  switch (sessionStatus) {
    case SessionStatus.scheduled:
      return MeetingStatus.waiting;
    case SessionStatus.in_progress:
      return MeetingStatus.started;
    case SessionStatus.completed:
      return MeetingStatus.ended;
    case SessionStatus.cancelled:
      return MeetingStatus.canceled;
    case SessionStatus.archived:
      // Archived sessions are considered ended for meeting status purposes
      return MeetingStatus.ended;
    default:
      // Fallback to waiting for unknown statuses
      return MeetingStatus.waiting;
  }
}

export function getMeetingInfo({
  zoom,
  googleMeet,
  videoConferenceType,
  status,
}: MeetingInfoProps): MeetingInfo | null {
  let provider: MeetingProvider | null = null;
  switch (videoConferenceType) {
    case VideoConferenceType.zoom:
      provider = zoom!;
      break;
    case VideoConferenceType.googleMeet:
      provider = googleMeet!;
      break;
    default:
      provider = null;
      break;
  }

  if (!provider) {
    return null;
  }

  // Map session status directly to meeting status (1:1 relationship)
  const meetingStatus = sessionStatusToMeetingStatus(status);

  const meetingStartInstant = getMeetingInstant(provider, 'startedAt');
  const meetingEndInstant = getMeetingInstant(provider, 'endedAt');

  const meeting: MeetingInfo = {
    kind: videoConferenceType,
    status: meetingStatus,
    startInstant: meetingStartInstant,
    endInstant: meetingEndInstant,
    provider,
  };

  return meeting;
}

function getMeetingInstant(
  provider?: MeetingProvider,
  key: keyof Omit<MeetingProvider, 'sessionId' | 'attendeeUrl' | 'hostUrl'> = 'startedAt',
): Temporal.Instant | null {
  const allInstant = provider?.[key] || [];

  return isNonEmptyArray(allInstant) ? minInstant(...allInstant) : null;
}

/**
 * True when a Zoom session is in progress but Zoom has never reported the teacher in the meeting.
 *
 * `coachJoinAt` is only written by Zoom webhooks (`meeting.started` / `participant_joined`), so an
 * empty array means the teacher started the session in Air Reading without the Zoom meeting starting.
 * Google Meet is excluded because it has no webhooks and its timestamps are synthetic.
 */
export function isZoomMeetingNotStarted(meeting?: MeetingInfo | null): boolean {
  return (
    meeting?.kind === VideoConferenceType.zoom &&
    meeting.status === MeetingStatus.started &&
    !isNonEmptyArray(meeting.provider?.coachJoinAt ?? [])
  );
}

export type PastWaitingSessionCandidate = Pick<
  AdminSession,
  'status' | 'endInstant' | 'meeting' | 'joinedStudentsCount'
> & {
  sessionFeedbacks: Pick<PlainSessionFeedback, 'feedbackSubmitted'>[];
};

/**
 * True when the platform already captured a signal that someone attended the class: the teacher
 * joined the meeting, a student joined, or the teacher submitted feedback.
 */
function hasSessionAttendanceEvidence(session: PastWaitingSessionCandidate): boolean {
  return (
    isNonEmptyArray(session.meeting?.provider?.coachJoinAt ?? []) ||
    session.joinedStudentsCount > 0 ||
    session.sessionFeedbacks.some(({ feedbackSubmitted }) => feedbackSubmitted)
  );
}

/**
 * True when an admin may complete a session that never left Waiting even though its scheduled end
 * time has passed. Meeting status is derived 1:1 from session status, so Waiting means
 * the session is still `scheduled`.
 *
 * Without attendance evidence the session is a real Teacher No Show and stays in the TNS workflow
 * rather than becoming manually completable.
 */
export function canCompletePastWaitingSession(
  session: PastWaitingSessionCandidate,
  now: Temporal.Instant = Temporal.Now.instant(),
): boolean {
  return (
    sessionStatusToMeetingStatus(session.status) === MeetingStatus.waiting &&
    InstantCompare(session.endInstant).isBefore(now) &&
    hasSessionAttendanceEvidence(session)
  );
}

export function getMostRecentSession(sessions: StudentSessionLike[], timeZone: string): StudentSessionLike | null {
  const today = Temporal.Now.zonedDateTimeISO(timeZone).startOfDay();

  const pastSessions = sessions.filter((session) => {
    const sessionEnd = Temporal.Instant.from(session.endInstant).toZonedDateTimeISO(timeZone);
    return Temporal.ZonedDateTime.compare(sessionEnd, today) <= 0;
  });

  if (pastSessions.length === 0) return null;

  return pastSessions.reduce((latest, current) => {
    const latestEnd = Temporal.Instant.from(latest.endInstant).toZonedDateTimeISO(timeZone);
    const currentEnd = Temporal.Instant.from(current.endInstant).toZonedDateTimeISO(timeZone);

    return Temporal.ZonedDateTime.compare(currentEnd, latestEnd) > 0 ? current : latest;
  });
}

export function getMostRecentLesson(sessions: StudentSessionLike[], timeZone: string): StudentSessionLike | null {
  const today = Temporal.Now.zonedDateTimeISO(timeZone).startOfDay();

  const pastSessions = sessions.filter((session) => {
    const sessionEnd = Temporal.Instant.from(session.endInstant).toZonedDateTimeISO(timeZone);
    return Temporal.ZonedDateTime.compare(sessionEnd, today) <= 0;
  });

  if (pastSessions.length === 0) return null;

  return pastSessions.reduce((latest, current) => {
    const latestEnd = Temporal.Instant.from(latest.endInstant).toZonedDateTimeISO(timeZone);
    const currentEnd = Temporal.Instant.from(current.endInstant).toZonedDateTimeISO(timeZone);

    return Temporal.ZonedDateTime.compare(currentEnd, latestEnd) > 0 ? current : latest;
  });
}

export function calculateSessionCancelationInfo(
  startInstant: Temporal.Instant,
  options?: { nowInstant: Temporal.Instant },
): {
  canceledAt: Date;
  hoursCanceledBeforeStart: number;
  cancelationTag: Extract<CoachSessionRemarkV2, 'canceled' | 'canceledWithin12Hours'>;
} {
  const canceledAtInstant = options?.nowInstant ?? Temporal.Now.instant();
  const canceledAt = instantToDate(canceledAtInstant);
  const millisUntilStart = startInstant.epochMilliseconds - canceledAtInstant.epochMilliseconds;
  const hoursCanceledBeforeStart = millisUntilStart / (60 * 60 * 1000);
  const cancelationTag =
    millisUntilStart <= 12 * 60 * 60 * 1000
      ? CoachSessionRemarkV2.canceledWithin12Hours
      : CoachSessionRemarkV2.canceled;

  return { canceledAt, hoursCanceledBeforeStart, cancelationTag };
}

export const ATTENDANCE_WINDOW_HOURS_BEFORE_START = 24;
export const ATTENDANCE_WINDOW_HOURS_AFTER_END = 8;

export function isWithinAttendanceWindow(
  startInstant: Temporal.Instant,
  endInstant: Temporal.Instant,
  nowOverrideForTestsOnly?: Temporal.Instant,
): boolean {
  const now = nowOverrideForTestsOnly ?? Temporal.Now.instant();
  const opensAt = startInstant.subtract({ hours: ATTENDANCE_WINDOW_HOURS_BEFORE_START });
  const closesAt = endInstant.add({ hours: ATTENDANCE_WINDOW_HOURS_AFTER_END });

  return Temporal.Instant.compare(now, opensAt) >= 0 && Temporal.Instant.compare(now, closesAt) <= 0;
}

export function isLessonLinkAvailable(lessonLink: string | null, startInstant: Temporal.Instant): boolean {
  if (!lessonLink) return false;

  const now = Temporal.Now.instant();

  const accessOpensAt = startInstant.subtract({ hours: 7 * 24 });
  const accessClosesAt = startInstant.add({ hours: 3 * 24 });

  const isWithinAccessWindow =
    Temporal.Instant.compare(now, accessOpensAt) >= 0 && Temporal.Instant.compare(now, accessClosesAt) <= 0;

  return isWithinAccessWindow;
}

//#region Makeup Diagnostic utilities

export const MAKEUP_DIAGNOSTIC_LESSON_PREFIX = 'Makeup Diagnostic';

export function isMakeupDiagnosticLesson(lessonName: string): boolean {
  return lessonName.startsWith(MAKEUP_DIAGNOSTIC_LESSON_PREFIX);
}

/** Family diagnostic sessions use the dedicated Trial lesson / learning path. */
export const TRIAL_LESSON_NAME = 'Trial';

export function isTrialLessonName(lessonName: string): boolean {
  return lessonName === TRIAL_LESSON_NAME;
}

export function getDiagnosticAssessmentLabel(needsDiagnosticAssessment: boolean | undefined): string {
  if (needsDiagnosticAssessment === true) return ' (Needs assessment)';
  if (needsDiagnosticAssessment === false) return ' (No assessment needed)';
  return '';
}

/**
 * Fluency session if the **class** learning path is a fluency path, or if the **scheduled lesson**
 * is a member of at least one fluency-titled learning path in the curriculum (via `LearningPathLesson`).
 */
export function isFluencySession(session: AdminSession): boolean {
  const groupPathHasFluency = session.learningPath?.name?.toLowerCase().includes('fluency') ?? false;
  const lessonOnFluencyPath = session.lesson.parentLearningPathNames.some((name) =>
    name.toLowerCase().includes('fluency'),
  );
  return groupPathHasFluency || lessonOnFluencyPath;
}

/**
 * Returns the assessment label for a student in a Makeup Diagnostic context
 * based on learningPathId and startingLessonId.
 * - Not leveled (no learningPathId or no startingLessonId) → "(Needs assessment)"
 * - Leveled → "(No assessment needed)"
 */
export function getNeedAssessmentLabelForMakeupDiagnostic(
  learningPathId: string | null | undefined,
  startingLessonId: string | null | undefined,
): '(Needs assessment)' | '(No assessment needed)' {
  const isLeveled = Boolean(learningPathId && startingLessonId);
  return isLeveled ? '(No assessment needed)' : '(Needs assessment)';
}

//#endregion

/**
 * Get the summary of the sub sessions with time change.
 * @param session The session to get the summary for.
 * @returns The summary of the sub sessions with time change.
 */
export const getSubSessionsWithTimeChangeSummary = (session: RecreateSubSessionTimeChangedSummary) => {
  const originalStart = Temporal.Instant.from(session.startInstant);
  const originalEnd = Temporal.Instant.from(session.endInstant);
  const newStart = Temporal.Instant.from(session.newStartInstant);
  const newEnd = Temporal.Instant.from(session.newEndInstant);
  const originalTime = `${formatShortDateWithDayOfWeek(originalStart, session.timeZone)}, ${formatTimeRange(originalStart, originalEnd, session.timeZone)}`;
  const newTime = `${formatShortDateWithDayOfWeek(newStart, session.timeZone)}, ${formatTimeRange(newStart, newEnd, session.timeZone)}`;
  return { originalTime, newTime };
};

/**
 * Get the label of the sub teacher.
 * @param session The session to get the label for.
 * @returns The label of the sub teacher.
 */
export const getSubTeacherLabel = (session: RecreateSessionSummaryItem): string => {
  if (!isNotNullish(session.subTeacherName)) {
    return 'pending sub teacher';
  } else if (session.previousSubUnavailable) {
    return `previously assigned sub teacher ${session.subTeacherName} unavailable, pending new sub teacher`;
  } else {
    return session.subTeacherName;
  }
};

/** Include cancelled rows so trailing cancelled session dates are visible to this helper. */
export type SessionForExtendAnchor = {
  start: Temporal.Instant;
  end: Temporal.Instant;
  status: SessionStatus;
};

/**
 * First instant to generate from when extending the schedule. Trailing cancelled sessions are skipped
 * (anchor after their end); otherwise the legacy rule uses the day after the last non-cancelled start.
 */
export function computeExtendLastSessionPlan<T extends SessionForExtendAnchor>(
  groupSessions: T[],
  timeZone: string,
): { startOfDayInstant: Temporal.Instant; lastNonCancelled: T } | null {
  if (groupSessions.length === 0) {
    return null;
  }

  const sorted = [...groupSessions].sort((a, b) => {
    const endCmp = Temporal.Instant.compare(a.end, b.end);
    if (endCmp !== 0) {
      return endCmp;
    }
    return Temporal.Instant.compare(a.start, b.start);
  });
  const lastNonCancelled = sorted.findLast((s) => s.status !== SessionStatus.cancelled);
  if (!lastNonCancelled) {
    return null;
  }

  const lastSession = sorted.at(-1)!;
  const anchorDate =
    lastSession.status === SessionStatus.cancelled
      ? lastSession.end.toZonedDateTimeISO(timeZone).toPlainDate()
      : lastNonCancelled.start.toZonedDateTimeISO(timeZone).toPlainDate();

  return {
    startOfDayInstant: startOfDayFromPlainDate(anchorDate.add({ days: 1 }), timeZone),
    lastNonCancelled,
  };
}
